import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCampaign } from '../hooks/useCampaigns'
import type { GeneratorParams } from '../lib/generator'
import { useNPCGenerator } from '../hooks/useNPCGenerator'
import { ShadowParametersForm } from '../components/creation/ShadowParametersForm'
import { NPCPreviewPanel } from '../components/creation/NPCPreviewPanel'
import { CampaignRoleProvider, useCampaignRole } from '../hooks/useCampaignRole'

export function NPCGeneratorPage() {
  const { id: campaignId } = useParams<{ id: string }>()
  return (
    <CampaignRoleProvider campaignId={campaignId}>
      <NPCGeneratorContent campaignId={campaignId!} />
    </CampaignRoleProvider>
  )
}

function NPCGeneratorContent({ campaignId }: { campaignId: string }) {
  const navigate = useNavigate()
  const { isGM } = useCampaignRole()
  const { data: campaign, isLoading } = useCampaign(campaignId)
  const { generate, save, clear, isGenerating, isReady, result, error } = useNPCGenerator(campaignId)

  useEffect(() => {
    if (!isGM) navigate(`/campaign/${campaignId}`, { replace: true })
  }, [isGM, campaignId, navigate])

  if (isLoading) return <div className="p-8 text-ink-light">Loading...</div>
  if (!campaign) return <div className="p-8 text-danger">Campaign not found.</div>

  const handleSave = async (): Promise<string | null> => {
    const charId = await save()
    if (charId) {
      navigate(`/character/${charId}`)
    }
    return charId
  }

  // Store last params so regenerate uses the same constraints
  const lastParamsRef = React.useRef<Record<string, unknown>>({})

  const handleGenerate = (params: GeneratorParams) => {
    lastParamsRef.current = params as Record<string, unknown>
    generate({ ...params, gameYear: campaign.current_tr_year })
  }

  const handleRegenerate = () => {
    generate({ ...lastParamsRef.current, gameYear: campaign.current_tr_year } as any)
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center gap-3 mb-4 border-b border-parchment-dark pb-3">
        <button onClick={() => navigate(`/campaign/${campaignId}`)}
          className="px-3 py-1 text-sm border border-parchment-dark rounded hover:bg-parchment-dark">
          &larr; Campaign
        </button>
        <h2 className="text-xl font-bold text-accent">Generate NPC</h2>
        <span className="text-ink-light text-sm">{campaign.name} — TR {campaign.current_tr_year}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-parchment-dark rounded-lg p-4">
          <ShadowParametersForm
            onGenerate={handleGenerate}
            isGenerating={isGenerating || !isReady}
            onCultureChange={clear}
          />
        </div>

        <div>
          {result ? (
            <NPCPreviewPanel
              npc={result}
              onSave={handleSave}
              onRegenerate={handleRegenerate}
              onClear={clear}
              error={error}
            />
          ) : (
            <div className="bg-white border border-parchment-dark rounded-lg p-8 text-center text-ink-light">
              <p className="text-lg mb-2">No NPC generated yet</p>
              <p className="text-sm">Set parameters on the left and click Generate, or leave everything blank for a completely random NPC.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
